import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import cloud from "d3-cloud";
import i18n from "../../../utils/ChrI18n";
import { getHoverOptions } from './commonUtils';

const ChrWordCloud = ({ wordCloudId, words, width = 350, height = 200, skipReRendersForSameWords = true, colorScale = [] }) => {
  const containerRef = useRef(null);
  const previousWordsRef = useRef(null);
  const hasWords = words && words.length > 0;
  
  // Adjust width if screen is mobile size
  const computedWidth = window.innerWidth <= 576 ? 325 : width;

  const CONFIG = {
    ANIMATION_DURATION: 800,
    WORD_PADDING: 2,
    FONT_FAMILY: "Inter",
    FONT_WEIGHT: "700",
    FONT_SIZE_RANGE: [12, 36],
    HOVER_OPACITY: 0.1,
    NORMAL_OPACITY: 1,
    SPIRAL_TYPE: "archimedean"
  };

  useEffect(() => {
    if (!hasWords) return;
    
    // Compare with previous words data to prevent unnecessary re-renders
    if (skipReRendersForSameWords && previousWordsRef.current && isSameWordsData(previousWordsRef.current, words)) {
      return; 
    }
    
    // Store current words for next comparison
    previousWordsRef.current = words;
    
    const svg = createSVGContainer();
    const { colorRangeScale, fontSizeScale } = createDataScales();
    
    const layout = createWordCloudLayout(fontSizeScale);
    layout.on("end", (data) => renderWordCloud(svg, data, colorRangeScale));
    layout.start();
  }, [words, width, height, colorScale]);

  // Zero state, Return with message if the word cloud has no data
  if (!hasWords) {
    return (
      <div id={wordCloudId} className="d-flex align-items-center justify-content-center text-muted">
        {i18n.t('metrics.meeting_rating_responses.without_response.description')}
      </div>
    );
  }

  const isSameWordsData = (previousWords, newWords) => {
    if (previousWords.length !== newWords.length) return false;
    const prevWordsSorted = [...previousWords].sort((a, b) => a.value - b.value);
    const newWordsSorted = [...newWords].sort((a, b) => a.value - b.value);
    for (let i = 0; i < prevWordsSorted.length; i++) {
      if (prevWordsSorted[i].text !== newWordsSorted[i].text || prevWordsSorted[i].value !== newWordsSorted[i].value) {
        return false;
      }
    }
    return true;
  }

  // Create SVG container
  const createSVGContainer = () => {
    return d3.select(containerRef.current)
      .html("") // Clear previous renders
      .append("svg")
      .attr("width", computedWidth)
      .attr("height", height);
  };

  // Create color and font size scales based on data
  const createDataScales = () => {
    const valueExtent = d3.extent(words, d => d.value);
    const colorRangeScale = d3.scaleLinear().domain(valueExtent).range(colorScale);
    const fontSizeScale = d3.scaleSqrt().domain(valueExtent).range(CONFIG.FONT_SIZE_RANGE);
    return { colorRangeScale, fontSizeScale };
  };

  // Configure word cloud layout algorithm
  const createWordCloudLayout = (fontSizeScale) => {
    return cloud()
      .size([computedWidth, height])
      .words(words)
      .padding(CONFIG.WORD_PADDING)
      .rotate(() => 0) // Keep text horizontal for readability
      .font(CONFIG.FONT_FAMILY)
      .fontWeight(CONFIG.FONT_WEIGHT)
      .spiral(CONFIG.SPIRAL_TYPE)
      .fontSize(d => fontSizeScale(d.value));
  };

  // Render the complete word cloud
  const renderWordCloud = (svg, data, colorRangeScale) => {
    const group = svg.append("g").attr("transform", `translate(${computedWidth / 2}, ${height / 2})`);
    
    // Create text elements with all styling, tooltips, and interactions
    const textElements = group.selectAll("text")
      .data(data)
      .enter()
      .append("text")
      .style("font-family", CONFIG.FONT_FAMILY)
      .style("font-weight", CONFIG.FONT_WEIGHT)
      .style("fill", d => colorRangeScale(d.value))
      .style("cursor", "pointer")
      .style("outline", "none")
      .style("-webkit-tap-highlight-color", "transparent")
      .attr("text-anchor", "middle")
      .attr("transform", d => `translate(${d.x}, ${d.y})`)
      .each(function(d) {
        const hoverOptions = getHoverOptions(`${d.text}: ${d.value}`);
        Object.keys(hoverOptions).forEach(key => {
          d3.select(this).attr(key, hoverOptions[key]);
        });
      })
      .text(d => d.text)
      // Add hover effects inline
      .on("mouseover", function() {
        group.selectAll("text").style("opacity", CONFIG.HOVER_OPACITY);
        d3.select(this).style("opacity", CONFIG.NORMAL_OPACITY);
      })
      .on("mouseout", function() {
        group.selectAll("text").style("opacity", CONFIG.NORMAL_OPACITY);
      });
    
    animateWordEntrance(textElements);
  };

  // Animate text elements with smooth entrance transition
  const animateWordEntrance = (textElements) => {
    textElements
      .style("opacity", 0)
      .style("font-size", "0px")
      .transition()
      .duration(CONFIG.ANIMATION_DURATION)
      .style("opacity", CONFIG.NORMAL_OPACITY)
      .style("font-size", d => `${d.size}px`);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }} id={wordCloudId} />
  );
};

export default ChrWordCloud;
